import IconButton from "../../packages/IconButton";

import DeleteRenderer from "../../components/AddCIPAndShutdown/deleteCellRender";
import EditRenderer from "../../components/AddCIPAndShutdown/editCellRender";
import ShareRenderer from "../../components/AddCIPAndShutdown/shareCellRender";
import DupontCheckbox from "../../components/DupontCheckbox";
import TooltipRenderer from "../../components/TooltipRenderer";
import { CustomMembraneCellRenderer } from "./manageAcess";
import { getIsValidAndValidTrains } from "./plantUtils";

import { ADVISOR_TYPES } from "../../constants/global.constants";

import { CorrectIcon, PendingIcon,WrongIcon } from "../../assets/icons";

import { dateFormatter, timeFormatter } from "../../utils/tableDataFormatter";

const PredictionStatusRenderer = (t, params, type) => {
  const { value, data } = params;
  const { ca_completion_count, ra_completion_count, num_trains } = data;

  let Icon = null;
  let tooltip = `${type === "ca" ? ca_completion_count : ra_completion_count}/${num_trains} ${t("benchMark.trainsPredictionDone")}`;

  if (value === 0) {
    Icon = WrongIcon;
  } else if (value === 1) {
    Icon = PendingIcon;
  } else if (value === 2) {
    Icon = CorrectIcon;
  }

  return Icon ? (
    <IconButton tooltip={tooltip} placement="bottom">
      <Icon className="prediction-status-icon" />
    </IconButton>
  ) : null;
};

export const PLANT_COL_DEF = (t, isBenchmarkMode, advisorType) => {
  const columns = [
    {
      headerName: t("ROOperationAdvisorTableStrings.name"),
      field: "name",
      sortable: true,
      flex: 1,
      minWidth: 200,
      pinned: "left",
      cellRenderer: (params) => {
        const { plant_id } = params.data;
        const { onPlantSelection, selectedPlants } = params.context || {};

        const isChecked =
          selectedPlants?.some((item) => item.plant_id === plant_id) || false;

        const getDisabledState = () =>
          !getIsValidAndValidTrains(params.data, advisorType).isValid;

        return (
          <div className="plant-name">
            {isBenchmarkMode && (
              <DupontCheckbox
                key={`plant-checkbox-${plant_id}`}
                id={`plant-select-${plant_id}`}
                labelText=""
                checked={isChecked}
                disabled={getDisabledState()}
                onChange={(isChecked) => {
                  if (onPlantSelection) {
                    onPlantSelection(params.data, isChecked);
                  }
                }}
              />
            )}
            <TooltipRenderer title={params.value} />
          </div>
        );
      },
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.city"),
      field: "city",
      sortable: true,
      flex: 1,
      minWidth: 100,
      cellRenderer: (params) => <TooltipRenderer title={params.value} />,
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.country"),
      field: "country",
      sortable: true,
      flex: 1,
      wrapText: true,
      minWidth: 150,
      cellRenderer: (params) => <TooltipRenderer title={params.value} />,
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.createdon"),
      field: "created_at",
      sortable: true,
      flex: 1,
      minWidth: 130,
      sort: "desc",
      cellRenderer: (params) => (
        <TooltipRenderer title={params.value} formatter={dateFormatter} />
      ),
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.createdBy"),
      field: "created_by",
      sortable: true,
      flex: 1,
      wrapText: true,
      minWidth: 160,
      cellRenderer: (params) => <TooltipRenderer title={params.value} />,
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.sharing"),
      field: "sharing",
      sortable: true,
      flex: 1,
      wrapText: true,
      minWidth: 150,
      cellRenderer: (params) => CustomMembraneCellRenderer({ ...params }),
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.modifiedBy"),
      field: "modified_by",
      sortable: true,
      flex: 1,
      minWidth: 160,
      cellRenderer: (params) => <TooltipRenderer title={params.value} />,
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.modifiedAt"),
      field: "modified_at",
      sortable: true,
      flex: 1,
      wrapText: true,
      minWidth: 200,
      cellRenderer: (params) => (
        <TooltipRenderer title={params.value} formatter={timeFormatter} />
      ),
    },
    {
      colId: "delete",
      lockPinned: true,
      pinned: "right",
      width: 50,
      headerName: t("ValueStrings.delete"),
      cellRenderer: DeleteRenderer,
    },
    {
      colId: "share",
      lockPinned: true,
      pinned: "right",
      width: 50,
      headerName: t("ValueStrings.share"),
      cellRenderer: ShareRenderer,
    },
    {
      colId: "edit",
      lockPinned: true,
      pinned: "right",
      width: 50,
      headerName: t("ValueStrings.edit"),
      cellRenderer: EditRenderer,
    },
  ];

  if (isBenchmarkMode) {
    const baseColumns = columns.filter(
      (col) => !["delete", "share", "edit"].includes(col.colId),
    );
    const nameColumn = baseColumns[0];
    const otherColumns = baseColumns.slice(1);

    const benchmarkColumns = [
      nameColumn,
      {
        colId: "cleaning_prediction",
        headerName: t("benchMark.cleaningReport"),
        field: "overall_ca_status",
        sortable: true,
        flex: 1,
        minWidth: 165,
        cellRenderer: (params) => PredictionStatusRenderer(t, params, "ca"),
        cellClass: "prediction-status-cell",
      },
      {
        colId: "lifetime_prediction",
        headerName: t("benchMark.lifetimeReport"),
        field: "overall_ra_status",
        sortable: true,
        flex: 1,
        minWidth: 165,
        cellRenderer: (params) => PredictionStatusRenderer(t, params, "ra"),
        cellClass: "prediction-status-cell",
      },
      ...otherColumns,
    ];
    return benchmarkColumns;
  }

  return columns;
};

export const advisorOptionsConfig = (t) => [
  { label: t("benchMark.cleaningAdvisor"), value: ADVISOR_TYPES.CA_ONLY },
  { label: t("benchMark.lifetimeAdvisor"), value: ADVISOR_TYPES.RA_ONLY },
  { label: t("ValueStrings.both"), value: ADVISOR_TYPES.BOTH },
];
